import React from 'react';
import {
  Stack,
  Text,
  Input,
  Select,
  Checkbox,
  Button,
  ControlLink,
  Overlay,
  SelectableOption,
  Clickable
} from '@moda/om';
import ExitIcon from '@moda/icons/exit-16';
import { hrefFor } from '../../routers';
import { tc } from '../../lib/trackingContext';
import { Loading } from '../Loading';
import { useModaPrivateForm } from './useModaPrivateForm';
import './ModaPrivateForm.scss';

type Props = { onSuccess: () => void; onClose: () => void };

const PRIORITY_AREA_OPTIONS: SelectableOption[] = [
  {
    label: 'The Edit: Closet Review + Wardrobe Update',
    value: 'The Edit: Closet Review + Wardrobe Update'
  },
  {
    label: 'The Essentials: Create a Capsule or Seasonal Wardrobe',
    value: 'The Essentials: Create a Capsule or Seasonal Wardrobe'
  },
  {
    label: 'The Exclusive: Private Sourcing + Gifting',
    value: 'The Exclusive: Private Sourcing + Gifting'
  },
  {
    label: 'The Event: Style for a Significant Occasion',
    value: 'The Event: Style for a Significant Occasion'
  },
  {
    label: 'The Refresh: Home Interior Finishing Touches',
    value: 'The Refresh: Home Interior Finishing Touches'
  },
  {
    label: 'The Routine: Skincare Consultation',
    value: 'The Routine: Skincare Consultation'
  }
];

const DESIRED_INVESTMENT_OPTIONS: SelectableOption[] = [
  { label: '$500-$2,500', value: '$500-$2,500' },
  { label: '$2,500-$5,000', value: '$2,500-$5,000' },
  { label: '$5,000-7,500', value: '$5,000-7,500' },
  { label: '$7,500-$10,000', value: '$7,500-$10,000' },
  { label: '$10,000+', value: '$10,000+' }
];

export const ModaPrivateForm: React.FC<Props> = ({ onSuccess, onClose }) => {
  const { handleSubmit, formErrors, error, loading } = useModaPrivateForm({ onSuccess });

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit(event);
  };

  const handleModalClose = () => {
    onClose();
    tc.track('Moda Private Form Closed', {
      addToTrackingEvent: {
        label: 'X',
        location: 'Moda Private Form Close button'
      }
    });
  };

  return (
    <form className="ModaPrivateForm" onSubmit={handleFormSubmit}>
      <Clickable
        className="ModaPrivateForm__close"
        aria-label="Close"
        onClick={handleModalClose}
        type="button"
      >
        <ExitIcon />
      </Clickable>
      <Stack space={5}>
        <Stack space={4}>
          <Text className="ModaPrivateForm__title" treatment="h3" family="serif">
            Apply Now
          </Text>
          <Text className="ModaPrivateForm__description">
            Moda Private offers members an exclusive insider shopping experience. Provide your
            detailed profile below and our team will review and be in touch soon.
          </Text>
          <Input label="Full Name" name="name" error={formErrors.name?.message} />
          <Input label="Email" name="email" error={formErrors.email?.message} />

          <Input label="Phone Number" name="phone" error={formErrors.phone?.message} />
          <Input label="How would you like to be contacted?" name="channel">
            <Select
              label=""
              options={[
                { label: 'Email', value: 'email' },
                { label: 'Phone', value: 'phone' }
              ]}
            />
          </Input>
          <Input
            label="Where are you located? (City, Country)"
            name="location"
            error={formErrors.location?.message}
          />
          <Text treatment="h5" family="serif">
            Your Style
          </Text>
          <Input
            label="Describe your personal style, style challenges, and style goals"
            name="message"
          />
          <Input label="Please provide the priority area for your new style vision" name="area">
            <Select label="" options={PRIORITY_AREA_OPTIONS} />
          </Input>
          <Input label="Desired Investment" name="investment">
            <Select label="" options={DESIRED_INVESTMENT_OPTIONS} />
          </Input>
          <Input label="Your Clothing Size" name="size" />
        </Stack>
        <Stack space={4}>
          <Checkbox name="terms">
            <>
              I accept the{' '}
              <ControlLink to={hrefFor.TermsAndConditionsPage()}>Terms & Conditions</ControlLink>
            </>
          </Checkbox>
          <Checkbox name="subscribe">Sign me up for special access and updates</Checkbox>
          {error && <Text color="code-red">{error.message}</Text>}
        </Stack>
        <Button type="submit" className="ModaPrivateForm__submit">
          Submit
        </Button>
      </Stack>
      <Overlay show={loading}>
        <Loading />
        <Text treatment="eyebrow">Sending</Text>
      </Overlay>
    </form>
  );
};
